import { useEffect, useState } from "react";
import $ from "jquery";

// Actions available from the record-management dropdown. All four are wired
// to POST /api/emails/{activate,deactivate,publish,unpublish} with the same
// { addresses } request shape (activate/deactivate: CPPCON-1297/1481;
// publish/unpublish: CPPCON-1379, both moved off PATCH - AWS Load Balancer
// doesn't support it). Each reports a result per direct email address (see
// the `results` prop), but the value type differs: activate/deactivate
// return a descriptive string (e.g. "already activated", "not found in
// db"); publish/unpublish return a plain boolean, so a `false` collapses
// "not found", "already in that state", and "org disables directory
// sharing" into one signal with no way to tell which applies. The
// Publishable column itself (ListEmails.js's PublishableDisplay) still isn't
// backed by the search API, so it reverts on refresh even though a
// successful Publish/Unpublish updates it locally right after the call.
export const ActionType = {
  ACTIVATE: "Activate",
  DEACTIVATE: "Deactivate",
  PUBLISH: "Publish",
  UNPUBLISH: "Unpublish",
  DELETE: "Delete",
};

// Maps an action to the Status column display value it represents once a
// record's status field exists.
export const ActionStatusDisplay = {
  [ActionType.ACTIVATE]: "Active",
  [ActionType.DEACTIVATE]: "Inactive",
  [ActionType.PUBLISH]: "Published",
  [ActionType.UNPUBLISH]: "Unpublished",
};

const ActionConfig = {
  [ActionType.ACTIVATE]: {
    title: "Activate Confirmation",
    description: "Selected item(s) will be activated.",
    prompt: "Are you sure you want to activate?",
    successMessage: "Activated Successfully!",
    confirmLabel: "Activate",
    processingLabel: "Activating...",
  },
  [ActionType.DEACTIVATE]: {
    title: "Deactivate Confirmation",
    description: "Selected item(s) will be deactivated.",
    prompt: "Are you sure you want to deactivate?",
    successMessage: "Deactivated Successfully!",
    confirmLabel: "Deactivate",
    processingLabel: "Deactivating...",
  },
  [ActionType.PUBLISH]: {
    title: "Publish Confirmation",
    description: "Selected item(s) will be published.",
    prompt: "Are you sure you want to publish?",
    successMessage: "Published Successfully!",
    confirmLabel: "Publish",
    processingLabel: "Publishing...",
    // CPPCON-1379: Vincent's PublishDirectEmails/UnpublishDirectEmails
    // endpoints take a bare array of directEmailIds - no reason field -
    // so there's nothing to collect here. Commented out rather than
    // removed in case that changes.
    // requireReason: true,
    // reasonHint: "Please provide a reason why you are publishing the mailbox.",
  },
  [ActionType.UNPUBLISH]: {
    title: "Unpublish Confirmation",
    description: "Selected item(s) will be unpublished.",
    prompt: "Are you sure you want to unpublish?",
    successMessage: "Unpublished Successfully!",
    confirmLabel: "Unpublish",
    processingLabel: "Unpublishing...",
    // requireReason: true,
    // reasonHint: "Please provide a reason why you are unpublishing the mailbox.",
  },
  [ActionType.DELETE]: {
    title: "Delete Confirmation",
    description: "Mailbox will be permanently deleted from your account.",
    prompt: "Are you sure you want to delete?",
    successMessage: "Deleted Successfully!",
    confirmLabel: "Delete",
    processingLabel: "Deleting...",
    requireReason: true,
    reasonHint: "Please provide a reason why you are deleting the mailbox.",
  },
};

/**
 * Reusable confirmation modal for record-management actions (Activate,
 * Deactivate, Publish, Unpublish, Delete). Renders messaging appropriate to
 * `action`, lists the selected items, and (for actions that require it)
 * collects a reason before calling `onConfirm`. `itemNote` can flag an item
 * in that list before the user confirms, e.g. that it's already in the state
 * the action would put it in. Once the action completes, `results` can
 * supply a per-item result message (keyed by `itemLabel`) to display
 * alongside the generic success message, e.g. the per-address outcomes
 * returned by PATCH /api/emails/activate.
 */
const ConfirmActionModal = ({
  modalId = "ConfirmAction",
  action,
  items = [],
  itemLabel = (item) => item?.directEmailAddress ?? "",
  itemKey = (item) => item?.id,
  // Optional per-item note shown next to the item in the confirmation list,
  // e.g. flagging that it's already in the state the action would put it in.
  itemNote = () => null,
  processing = false,
  successFlag = false,
  errorMessage = "",
  results = null,
  onConfirm,
  onClose,
}) => {
  const [reasonInput, setReasonInput] = useState("");
  const [submitFlag, setSubmitFlag] = useState(false);

  const config = ActionConfig[action] ?? ActionConfig[ActionType.DELETE];
  const reasonMandatory = config.reasonMandatory ?? true;

  useEffect(() => {
    setReasonInput("");
    setSubmitFlag(false);
  }, [action]);

  const closeModal = () => {
    setReasonInput("");
    setSubmitFlag(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    $(`#${modalId}`).modal("hide");
    onClose?.();
  };

  const handleConfirmClick = () => {
    if (config.requireReason && reasonMandatory && reasonInput === "") {
      setSubmitFlag(true);
      return;
    }
    const reason =
      config.requireReason && reasonInput === "" ? "Not specified" : reasonInput;
    onConfirm?.(reason);
  };

  return (
    <div
      className="modal fade"
      id={modalId}
      data-backdrop="static"
      tabIndex="-1"
      role="dialog"
      aria-labelledby={`${modalId}Title`}
      aria-modal="true"
      aria-describedby={`${modalId}Desc`}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id={`${modalId}Title`}>
              {config.title}
            </h3>
            <button
              type="button"
              className="close"
              aria-label={`Close ${config.title}`}
              onClick={closeModal}
              disabled={processing}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {successFlag ? (
              <div>
                <div className="bi-success-check">
                  <i className="bi bi-check2-circle" aria-hidden="true"></i>
                </div>
                <div
                  id={`${modalId}Desc`}
                  className="message-wrapper"
                  role="alert"
                  aria-live="polite"
                >
                  {config.successMessage}
                </div>
                {results ? (
                  <ul aria-label={`${config.title} results`}>
                    {Object.entries(results).map(([label, message]) => (
                      <li key={label}>
                        {label}: {message}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <>
                {items.length === 0 ? (
                  <div className="ctr sp-pad3" role="alert" aria-live="polite">
                    There was nothing selected.
                  </div>
                ) : (
                  <>
                    <div className="field-wrapper">
                      <div className="spacer-margin7">
                        <p id={`${modalId}Prompt`}>{config.prompt} {config.description}</p>
                      </div>
                      <ul
                        aria-labelledby={`${modalId}Prompt`}
                      >
                        {items.map((el) => {
                          const note = itemNote(el);
                          return (
                            <li key={itemKey(el)}>
                              {itemLabel(el)}
                              {note ? <span className="or-clr text-size1"> - {note}</span> : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    {config.requireReason && reasonMandatory ? (
                      <div className="modal-content-body sp-margin14">
                        <label htmlFor={`${modalId}Reason`}>Reason</label>
                        <span aria-hidden="true" className="rd-clr"> *</span>
                        <input
                          type="text"
                          className="form-control"
                          id={`${modalId}Reason`}
                          name="ActionReason"
                          aria-describedby={`${modalId}ReasonDesc`}
                          value={reasonInput}
                          onChange={(e) => setReasonInput(e.target.value)}
                        />
                        <div id={`${modalId}ReasonDesc`} className="sp-margin3">
                          {config.reasonHint}
                        </div>
                        {submitFlag && reasonMandatory && reasonInput === "" ? (
                          <div
                            className="alert alert-danger field-wrapper"
                            role="alert"
                            aria-live="assertive"
                            aria-atomic="true"
                          >
                            Reason field is required.
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
                {errorMessage ? (
                  <p
                    className="alert alert-danger"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    dangerouslySetInnerHTML={{ __html: errorMessage }}
                  />
                ) : null}
              </>
            )}
          </div>
          <div className="modal-footer">
            {items.length === 0 || successFlag ? null : (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleConfirmClick}
                disabled={processing}
              >
                {processing ? config.processingLabel : config.confirmLabel}
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeModal}
              disabled={processing}
            >
              {items.length === 0 || successFlag ? "Close" : "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
